import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useParentContext, type ParentChild } from "@/lib/parent-context";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parent/messages")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Messages — MAKAI" }] }),
  component: MessagesPage,
});

interface Thread {
  teacherUserId: string;
  teacherName: string;
  learnerId: string | null;
  learnerName: string | null;
  last: { body: string; created_at: string; sender: string; read_at: string | null };
  unread: number;
}

interface Msg {
  id: string;
  body: string;
  subject: string | null;
  sender: "teacher" | "parent";
  created_at: string;
  read_at: string | null;
  teacher_user_id: string;
  parent_user_id: string;
  learner_id: string | null;
}

function MessagesPage() {
  const { ctx } = useParentContext();
  const [parentUserId, setParentUserId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [active, setActive] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setParentUserId(user.id);
    })();
  }, []);

  useEffect(() => {
    if (!parentUserId || !ctx) return;
    let cancelled = false;
    (async () => {
      const { data: msgs } = await supabase
        .from("teacher_messages")
        .select("id, body, subject, sender, created_at, read_at, teacher_user_id, parent_user_id, learner_id")
        .eq("parent_user_id", parentUserId)
        .order("created_at", { ascending: false });
      const teacherIds = Array.from(new Set((msgs ?? []).map((m) => m.teacher_user_id)));
      const profiles: Record<string, string> = {};
      if (teacherIds.length) {
        const { data: pf } = await supabase.from("profiles").select("id, full_name").in("id", teacherIds);
        for (const p of pf ?? []) profiles[p.id] = p.full_name || "Teacher";
      }
      const learnerMap: Record<string, string> = {};
      for (const c of ctx.children) learnerMap[c.learnerId] = c.fullName;

      const grouped = new Map<string, Thread>();
      for (const m of msgs ?? []) {
        const key = `${m.teacher_user_id}::${m.learner_id ?? ""}`;
        const existing = grouped.get(key);
        if (!existing) {
          grouped.set(key, {
            teacherUserId: m.teacher_user_id,
            teacherName: profiles[m.teacher_user_id] ?? "Teacher",
            learnerId: m.learner_id,
            learnerName: m.learner_id ? learnerMap[m.learner_id] ?? null : null,
            last: { body: m.body, created_at: m.created_at, sender: m.sender, read_at: m.read_at },
            unread: !m.read_at && m.sender === "teacher" ? 1 : 0,
          });
        } else if (!m.read_at && m.sender === "teacher") {
          existing.unread += 1;
        }
      }
      if (!cancelled) setThreads([...grouped.values()]);
    })();
    return () => { cancelled = true; };
  }, [parentUserId, ctx]);

  useEffect(() => {
    if (!active || !parentUserId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("teacher_messages")
        .select("id, body, subject, sender, created_at, read_at, teacher_user_id, parent_user_id, learner_id")
        .eq("parent_user_id", parentUserId)
        .eq("teacher_user_id", active.teacherUserId)
        .order("created_at", { ascending: true });
      const filtered = (data ?? []).filter((m) => (active.learnerId ? m.learner_id === active.learnerId : !m.learner_id));
      if (cancelled) return;
      setMessages(filtered as Msg[]);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      // mark as read
      const unreadIds = filtered.filter((m) => !m.read_at && m.sender === "teacher").map((m) => m.id);
      if (unreadIds.length) await supabase.from("teacher_messages").update({ read_at: new Date().toISOString() }).in("id", unreadIds);
    })();
    return () => { cancelled = true; };
  }, [active, parentUserId]);

  const send = async () => {
    if (!active || !text.trim() || !parentUserId) return;
    setSending(true);
    const child = ctx?.children.find((c) => c.learnerId === active.learnerId);
    const schoolId = child?.schoolId ?? ctx?.children[0]?.schoolId;
    if (!schoolId) { setSending(false); return; }
    const { data, error } = await supabase.from("teacher_messages").insert({
      school_id: schoolId,
      teacher_user_id: active.teacherUserId,
      parent_user_id: parentUserId,
      learner_id: active.learnerId,
      body: text.trim(),
      sender: "parent",
    }).select("id, body, subject, sender, created_at, read_at, teacher_user_id, parent_user_id, learner_id").maybeSingle();
    setSending(false);
    if (error || !data) return;
    setMessages((m) => [...m, data as Msg]);
    setText("");
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  return (
    <AppShell role="parent" title="Teacher messages" subtitle="Direct, private conversations with your child's teachers.">
      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-2xl border border-border bg-card p-2 shadow-soft">
          {threads.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">No conversations yet. A teacher will write to you when there's news.</p>
          ) : threads.map((t) => (
            <button key={`${t.teacherUserId}-${t.learnerId}`} type="button" onClick={() => setActive(t)}
              className={cn("flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left",
                active?.teacherUserId === t.teacherUserId && active.learnerId === t.learnerId ? "bg-primary-soft" : "hover:bg-muted")}>
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-gold text-xs font-bold text-gold-foreground">
                {t.teacherName.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold">{t.teacherName}</span>
                  {t.unread > 0 && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">{t.unread}</span>}
                </div>
                {t.learnerName && <div className="text-[10px] uppercase text-muted-foreground">{t.learnerName}</div>}
                <p className="line-clamp-1 text-xs text-muted-foreground">{t.last.body}</p>
              </div>
            </button>
          ))}
        </aside>

        <section className="flex h-[70vh] flex-col rounded-2xl border border-border bg-card shadow-soft">
          {!active ? (
            <div className="grid flex-1 place-items-center text-center text-sm text-muted-foreground">
              <div>
                <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-50" />
                Pick a conversation to read or reply.
              </div>
            </div>
          ) : (
            <>
              <header className="border-b border-border px-4 py-3">
                <div className="font-display text-sm font-bold">{active.teacherName}</div>
                {active.learnerName && <div className="text-[11px] text-muted-foreground">About {active.learnerName}</div>}
              </header>
              <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
                {messages.map((m) => (
                  <div key={m.id} className={cn("flex", m.sender === "parent" ? "justify-end" : "justify-start")}>
                    <div className={cn("max-w-[80%] rounded-2xl px-3 py-2 text-sm",
                      m.sender === "parent" ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md bg-muted")}>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <div className={cn("mt-1 text-[10px]",
                        m.sender === "parent" ? "text-primary-foreground/70" : "text-muted-foreground")}>
                        {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border p-3">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…"
                  className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <button type="submit" disabled={sending || !text.trim()}
                  className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </AppShell>
  );
}
